import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { ImageOff } from "lucide-react";

/* Photos are only reachable through the authenticated /api/media endpoint,
   so <img src> cannot be used directly — we fetch a blob with the bearer
   token and render an object URL.

   Performance (low-CPU hosts):
   - module-level cache: each photo blob is fetched ONCE per session and its
     object URL is reused across remounts/pages (photos are immutable);
   - lazy loading: thumbnails are only fetched when they scroll near the
     viewport, so a 100-row punch list doesn't fire 100 simultaneous requests;
   - one automatic retry on failure (transient stalls under CPU contention). */

const CACHE_MAX = 400;
const cache = new Map(); // key -> Promise<objectURL>

const fetchUrl = (photoId, thumb) => {
  const key = `${photoId}|${thumb ? 1 : 0}`;
  if (cache.has(key)) return cache.get(key);
  const p = api
    .get(`/media/${photoId}`, { params: { thumb }, responseType: "blob" })
    .then((res) => URL.createObjectURL(res.data))
    .catch((err) => {
      cache.delete(key); // don't cache failures — allow future retries
      throw err;
    });
  if (cache.size >= CACHE_MAX) {
    const oldest = cache.keys().next().value;
    cache.delete(oldest); // object URL intentionally not revoked: may be in use
  }
  cache.set(key, p);
  return p;
};

export const AuthImage = ({ photoId, thumb = true, className, alt = "punch photo", testId }) => {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  const [visible, setVisible] = useState(false);
  const [attemptKey, setAttemptKey] = useState(0);
  const ref = useRef(null);

  // Only start fetching when the element is near the viewport.
  useEffect(() => {
    if (visible) return undefined;
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return undefined;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { rootMargin: "300px" },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [visible]);

  useEffect(() => {
    if (!visible) return undefined;
    let cancelled = false;
    if (!photoId) {
      setFailed(true);
      return undefined;
    }
    setFailed(false);
    const load = (attempt) =>
      fetchUrl(photoId, thumb)
        .then((u) => !cancelled && setUrl(u))
        .catch((err) => {
          if (cancelled) return;
          const status = err?.response?.status;
          // 404 = photo genuinely gone (e.g. purged) — retrying is pointless
          if (status === 404) {
            setFailed(true);
            return;
          }
          // transient (timeout / 5xx under CPU contention): retry with backoff
          if (attempt < 2) setTimeout(() => !cancelled && load(attempt + 1), 1500 * (attempt + 1));
          else setFailed(true);
        });
    load(0);
    return () => {
      cancelled = true;
    };
  }, [photoId, thumb, visible, attemptKey]);

  if (failed) {
    return (
      <div
        ref={ref}
        role="button"
        tabIndex={0}
        data-testid="auth-image-retry"
        title="Tap to retry"
        onClick={(e) => {
          e.stopPropagation(); // don't trigger the parent thumbnail button
          setFailed(false);
          setAttemptKey((k) => k + 1);
        }}
        className={cn("flex cursor-pointer items-center justify-center bg-secondary text-muted-foreground", className)}
      >
        <ImageOff className="h-5 w-5" />
      </div>
    );
  }
  if (!url) {
    return <div ref={ref} className={cn("animate-pulse bg-secondary", className)} />;
  }
  return <img ref={ref} src={url} alt={alt} data-testid={testId} className={cn("object-cover", className)} />;
};
